type CallableFailure = Readonly<{ code?: unknown; customData?: { httpStatus?: unknown; time?: unknown } }>;

const transientSessionReadCodes = new Set([
  'functions/internal',
  'functions/unavailable',
  'functions/resource-exhausted',
  'functions/deadline-exceeded',
  'appCheck/fetch-network-error',
  'appCheck/network-request-failed',
]);

const sessionReadFailureCode = (error: unknown) =>
  typeof error === 'object' && error !== null
    ? String((error as CallableFailure).code ?? '')
    : '';

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => globalThis.setTimeout(resolve, milliseconds));

/** Keep only a stable reason code at the UI boundary; never expose token/provider messages. */
function securityFailure(error: unknown): unknown {
  const failure = error as CallableFailure | null;
  if (!sessionReadFailureCode(error).startsWith('appCheck/')) return error;
  const status = Number(failure?.customData?.httpStatus);
  const reason = status === 401 || status === 403 ? 'verification-rejected'
    : status === 404 || status === 400 ? 'configuration-error'
    : status === 429 ? 'rate-limited'
    : status >= 500 && status <= 599 ? 'service-unavailable' : null;
  return reason ? Object.assign(new Error('Security verification incomplete'), { code: `appCheck/${reason}` }) : error;
}
function serviceRetryDelay(error: unknown): number | null {
  const code = sessionReadFailureCode(error), failure = error as CallableFailure | null;
  const status = Number(failure?.customData?.httpStatus);
  if (!code.startsWith('appCheck/') || ![500, 502, 503, 504].includes(status)) return null;
  if (code === 'appCheck/fetch-status-error') return 500;
  if (!['appCheck/initial-throttle', 'appCheck/throttled'].includes(code)) return null;
  // Honor the SDK's backoff. Long/unknown waits and rate/policy rejection never loop.
  const duration = /^00m:(0[0-3])s$/.exec(String(failure?.customData?.time ?? ''));
  return duration ? Number(duration[1]) * 1000 + 600 : null;
}

/**
 * `loadIdentitySession` is a read-only admission gate. The Functions SDK maps an
 * App Check verifier outage to `functions/unauthenticated`, the same code used
 * for a rejected token. Refresh exactly once and repeat this read once: a stale
 * browser token or a recovered verifier can proceed, while a rejected token and
 * every authority error remain blocked.
 */
export async function retryIdentitySessionRead<T>(
  read: () => Promise<T>,
  refreshAppCheckToken: () => Promise<unknown>,
  delays: readonly number[] = [500, 1_200],
): Promise<T> {
  let retry = 0;
  let appCheckRefreshUsed = false;
  let executionRetryUsed = false;
  const recoverTransient = async <R>(operation: () => Promise<R>): Promise<R> => {
    for (;;) {
      try { return await operation(); }
      catch (error) {
        const delay = delays[retry], code = sessionReadFailureCode(error), serviceDelay = serviceRetryDelay(error);
        const executionRetry = code === 'appCheck/recaptcha-error' && !executionRetryUsed;
        if ((!transientSessionReadCodes.has(code) && !executionRetry && serviceDelay === null) || delay === undefined)
          throw securityFailure(error);
        if (executionRetry) executionRetryUsed = true;
        retry += 1;
        await wait(Math.max(delay, serviceDelay ?? 0));
      }
    }
  };
  for (;;) {
    try {
      return await recoverTransient(read);
    } catch (error) {
      const code = sessionReadFailureCode(error);
      if (code === 'functions/unauthenticated' && !appCheckRefreshUsed) {
        appCheckRefreshUsed = true;
        await recoverTransient(refreshAppCheckToken);
      } else {
        throw error;
      }
    }
  }
}
